export const MAX_FILE_SIZE_MB = 10;
export const ALLOWED_FILE_TYPES = ['pdf', 'jpg', 'jpeg', 'png'] as const;

export type FileValidationError = {
    code: 'invalid_type' | 'file_too_large';
    message: string;
};

export interface FileValidationResult {
    isValid: boolean;
    error?: FileValidationError;
}

/**
 * Validates a file based on size and type
 * @param file - The file to validate
 * @param options - Optional validation options
 * @returns FileValidationResult object containing validation status and any error
 */
export const validateFile = (
    file: File,
    options?: {
        maxSizeMB?: number;
        allowedTypes?: readonly string[];
    },
): FileValidationResult => {
    const maxSize = (options?.maxSizeMB || MAX_FILE_SIZE_MB) * 1024 * 1024; // Convert to bytes
    const allowedTypes = options?.allowedTypes || ALLOWED_FILE_TYPES;

    // Check file size
    if (file.size > maxSize) {
        return {
            isValid: false,
            error: {
                code: 'file_too_large',
                message: `File size must be less than ${MAX_FILE_SIZE_MB}MB`,
            },
        };
    }

    // Check file type
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !allowedTypes.includes(fileExtension)) {
        return {
            isValid: false,
            error: {
                code: 'invalid_type',
                message: `File type must be one of: ${allowedTypes.join(', ')}`,
            },
        };
    }

    return { isValid: true };
};

/**
 * Formats bytes into a human-readable string
 * @param bytes - The number of bytes
 * @returns Formatted string (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Creates a file validation schema for Zod
 * @param options - Optional validation options
 * @returns Zod schema for file validation
 */
export const createFileValidationSchema = () => {
    return {
        maxSizeMB: MAX_FILE_SIZE_MB,
        allowedTypes: ALLOWED_FILE_TYPES,
        validateFile,
    };
};
